import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { customAlphabet } from 'nanoid';
import { useAuth } from '../../../utils/Auth';
import { saveCategoryData } from '../../../utils/QRFunctions/qrActions.js';
import API from '../../../API/index.js'
import { characterLengthCheck } from '../../../lib/characterLengthCheck';
import styles from './Categories.module.css';

const nanoidAlphabet = '1234567890abcdefghijklmnopqrstuvwxyzQWERTYUIOPASDFGHJKLZXCVBNM';
const nanoidLength = 6;
const MAX_CATEGORY_NAME_LENGTH = 30;

const isCategoryIdTaken = (categoriesArray, newId) => {
    return categoriesArray.some(cat => cat.id === newId);
};

export default function Categories(props) {
    const { noNewCategoriesBool, reference } = props;

    const { qr_data, dataDispatch, orgObj, categoryQrCounts, activeOrgId } = useAuth();

    const categories = useMemo(() => orgObj?.categories || [], [orgObj?.categories]);

    const sortedCategories = useMemo(() => {
        return [...categories].sort((a, b) => a.name.localeCompare(b.name));
    }, [categories]);

    const generateNanoid = useMemo(() => customAlphabet(nanoidAlphabet, nanoidLength), []);

    const [ addNewCategory, setAddNewCategory ] = useState(false);
    const [ newCategoryName, setNewCategoryName ] = useState('');
    const [ selectedCategory, setSelectedCategory ] = useState(() => {
        if (qr_data?.data?.category) {
            return categories.find(cat => cat.id === qr_data.data.category) || null;
        }
        return null;
    });
    const [ editMode, setEditMode ] = useState(false);
    const [ categoryToEdit, setCategoryToEdit ] = useState(null);
    const [ confirmingDelete, setConfirmingDelete ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState('');
    const [ successMsg, setSuccessMsg ] = useState('');
    const didInitRef = useRef(false);

    const allowNewCategories = useMemo(() => {
        if (noNewCategoriesBool) return false;
        return (orgObj?.plan?.categories === undefined || orgObj.plan.categories > categories.length);
    },[categories.length, noNewCategoriesBool, orgObj?.plan]);

    const appliedCategoryId = qr_data?.data?.category || null;

    const { createNewCategory, updateCategories: updateCategoriesAPI } = API;

    useEffect(() => {
        if (addNewCategory || selectedCategory?.id === 'new' || reference === 'new') return;
        if (didInitRef.current) return;

        const catId = qr_data?.data?.category;
        if (catId && categories?.length) {
            const foundCategory = categories.find(cat => cat.id === catId);
            if (foundCategory) {
                setSelectedCategory(foundCategory);
                setEditMode(true);
                setCategoryToEdit(foundCategory);
                setNewCategoryName(foundCategory.name);
                didInitRef.current = true;
            }
        } else if (!catId) {
            setSelectedCategory(null);
            setEditMode(false);
            setCategoryToEdit(null);
            setNewCategoryName('');
            didInitRef.current = true;
        }
    }, [qr_data?.data?.category, categories, addNewCategory, reference]);

    useEffect(() => {
        if (reference === 'new' && allowNewCategories) {
            setAddNewCategory(true);
            setSelectedCategory({ id: 'new', name: 'Add New Category' });
            setEditMode(false);
            setCategoryToEdit(null);
            setNewCategoryName('');
            setError('');
            setConfirmingDelete(false);
        }
    }, [reference, allowNewCategories]);

    useEffect(() => {
        if (successMsg) {
            const t = setTimeout(() => setSuccessMsg(''), 2500);
            return () => clearTimeout(t);
        }
    }, [successMsg]);

    const getUniqueCategoryId = useCallback(() => {
        let id;
        do {
            id = generateNanoid();
        } while (isCategoryIdTaken(categories, id));
        return id;
    }, [generateNanoid, categories]);

    const handleCategorySelect = useCallback(async (e) => {
        const selectedValue = e.target.value;
        setError('');

        if (selectedValue === 'new') {
            setAddNewCategory(true);
            setSelectedCategory({ id: 'new', name: 'Add New Category' });
            setNewCategoryName('');
            setEditMode(false);
            setCategoryToEdit(null);
            setConfirmingDelete(false);
        } else {
            setAddNewCategory(false);
            setNewCategoryName('');
            const chosenCategory = categories.find(cat => cat.id === selectedValue);
            setSelectedCategory(chosenCategory || null);
            if (chosenCategory) {
                setEditMode(true);
                setCategoryToEdit(chosenCategory);
                setNewCategoryName(chosenCategory.name);
            } else {
                setEditMode(false);
                setCategoryToEdit(null);
            }
            setConfirmingDelete(false);
        }
    }, [categories, dataDispatch, reference, qr_data]);

    const handleNewCategoryNameChange = useCallback((e) => {
        setNewCategoryName(e.target.value);
        setError('');
    }, []);

    const handleAddNewCategory = useCallback(async () => {
        const trimmedName = newCategoryName.trim();

        if (!trimmedName) {
            setError('Category name cannot be empty.');
            return;
        }

        if (!characterLengthCheck(trimmedName, MAX_CATEGORY_NAME_LENGTH)) {
            setError(`Category name must be ${MAX_CATEGORY_NAME_LENGTH} characters or fewer.`);
            return;
        }

        if (categories.some(cat => cat.name.toLowerCase() === trimmedName.toLowerCase())) {
            setError('A category with this name already exists.');
            return;
        }

        setLoading(true);
        setError('');

        const newCatId = getUniqueCategoryId();
        const categoryToAdd = { id: newCatId, name: trimmedName };

        try {
            const updatedCategories = [...categories, categoryToAdd];
            await updateCategoriesAPI({ org_id: activeOrgId, categories: updatedCategories });
            await createNewCategory({ org_id: activeOrgId, category: categoryToAdd });
            
            // Show the newly added category
            setSelectedCategory(categoryToAdd);
            setEditMode(true);
            setCategoryToEdit(categoryToAdd);
            setNewCategoryName(categoryToAdd.name);
            setAddNewCategory(false);
            setSuccessMsg('Category added');
        } catch (err) {
            console.error('Error adding new category:', err);
            setError('Could not add new category. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [newCategoryName, categories, updateCategoriesAPI, createNewCategory, getUniqueCategoryId]);

    const handleEditCategory = useCallback((category) => {
        setEditMode(true);
        setCategoryToEdit(category);
        setNewCategoryName(category.name);
        setAddNewCategory(false);
        setError('');
        setConfirmingDelete(false);
    }, []);

    const handleSaveEdit = useCallback(async () => {
        const trimmedName = newCategoryName.trim();

        if (!trimmedName) {
            setError('Category name cannot be empty.');
            return;
        }

        if (!characterLengthCheck(trimmedName, MAX_CATEGORY_NAME_LENGTH)) {
            setError(`Category name must be ${MAX_CATEGORY_NAME_LENGTH} characters or fewer.`);
            return;
        }

        if (categories.some(cat => cat.id !== categoryToEdit.id && cat.name.toLowerCase() === trimmedName.toLowerCase())) {
            setError('A category with this name already exists.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const updatedCategories = categories.map(cat => 
                cat.id === categoryToEdit.id ? { ...cat, name: trimmedName } : cat
            );
            await updateCategoriesAPI({ org_id: activeOrgId, categories: updatedCategories });

            setSuccessMsg('Category updated');
        } catch (err) {
            console.error('Error updating category:', err);
            setError('Could not update category. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [newCategoryName, categories, categoryToEdit, updateCategoriesAPI]);

    const handleDeleteCategory = useCallback(async (category) => {
        setLoading(true);
        setError('');

        try {
            const updatedCategories = categories.filter(cat => cat.id !== category.id);
            await updateCategoriesAPI({ org_id: activeOrgId, categories: updatedCategories });

            setEditMode(false);
            setCategoryToEdit(null);
            setNewCategoryName('');
            setConfirmingDelete(false);
            setSuccessMsg('Category deleted');
        } catch (err) {
            console.error('Error deleting category:', err);
            setError('Could not delete category. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [categories, updateCategoriesAPI]);

    const handleCancelEdit = useCallback(() => {
        setEditMode(false);
        setCategoryToEdit(null);
        setNewCategoryName('');
        setError('');
        setConfirmingDelete(false);
    }, []);

    const handleApplyCategoryToQR = useCallback(async (category) => {
        setSelectedCategory(category);
        dataDispatch({ type: 'category', data: category.id });

        if (reference !== 'new') {
            try {
                await saveCategoryData(qr_data?.id, category.id)();
                setSuccessMsg('Category applied to QR');
            } catch (saveError) {
                console.error('Error saving category data:', saveError);
                setError('Failed to save category selection.');
            }
        }
    }, [dataDispatch, reference, qr_data]);

    const handleListItemClick = useCallback((category) => {
        handleEditCategory(category);
    }, [handleEditCategory]);

    return (
        <>
        {/* Mobile View - Dropdown + Actions */}
        <div className={styles.mobileView}>
            <div className={styles.inputGroupSelect}>
                <select
                    className={styles.select}
                    value={selectedCategory?.id || '0'}
                    onChange={handleCategorySelect}
                >
                    <option value={'0'} disabled>Select Category</option>
                    {allowNewCategories && (
                        <option value={'new'}>
                            {'Add New Category'}
                        </option>
                    )}
                    {sortedCategories.length > 0 &&
                        sortedCategories.map(cat => (
                            <option value={cat.id} key={cat.id}>
                                {cat.name}
                            </option>
                        ))
                    }
                </select>
            </div>

            {successMsg && (
                <div className={styles.successToast}>{successMsg}</div>
            )}

            {addNewCategory && (
                <>
                    <div className={styles.inputGroup}>
                        <input
                            id='new-cat-input'
                            className={styles.input}
                            value={newCategoryName}
                            onChange={handleNewCategoryNameChange}
                                disabled={loading}
                                maxLength={MAX_CATEGORY_NAME_LENGTH}
                        />
                        <label htmlFor='new-cat-input' className={styles.label}>Category Name</label>
                    </div>
                    {error && <div className={styles.error}>{error}</div>}
                    {/* Row 1: Apply (disabled for new) */}
                    <div className={styles.mobileActionsRowLeft}>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            disabled={true}
                        >
                            <i className="fa-solid fa-check" />
                            Apply to QR
                        </button>
                    </div>
                    {/* Row 2: Cancel + Add */}
                    <div className={styles.mobileActionsRowRight}>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => {
                                setAddNewCategory(false);
                                setNewCategoryName('');
                                setError('');
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={handleAddNewCategory}
                                disabled={!characterLengthCheck(newCategoryName, MAX_CATEGORY_NAME_LENGTH, { trim: true }) || loading}
                        >
                            <i className="fa-regular fa-save" />
                            Add
                        </button>
                    </div>
                    {/* Divider */}
                    <div className={styles.mobileDivider} />
                    {/* Row 3: Delete (disabled for new) */}
                    <div className={styles.mobileActionsRowRight}>
                        <button
                            type="button"
                            className={styles.btnDanger}
                            disabled={true}
                        >
                            <i className="fa-regular fa-trash" />
                            Delete
                        </button>
                    </div>
                </>
            )}

            {!addNewCategory && selectedCategory && selectedCategory.id !== '0' && selectedCategory.id !== 'new' && (
                <>
                    <div className={styles.inputGroup}>
                        <input
                            id='edit-cat-input-mobile'
                            className={styles.input}
                            value={newCategoryName}
                            onChange={handleNewCategoryNameChange}
                                disabled={loading}
                                maxLength={MAX_CATEGORY_NAME_LENGTH}
                        />
                        <label htmlFor='edit-cat-input-mobile' className={styles.label}>Edit Name</label>
                    </div>
                    {error && <div className={styles.error}>{error}</div>}
                    {/* Row 1: Apply to QR */}
                    <div className={styles.mobileActionsRowLeft}>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={() => handleApplyCategoryToQR(selectedCategory)}
                            disabled={!characterLengthCheck(newCategoryName, MAX_CATEGORY_NAME_LENGTH, { trim: true }) || loading}
                        >
                            <i className="fa-solid fa-check" />
                            Apply to QR
                        </button>
                    </div>
                    {/* Row 2: Cancel + Save */}
                    <div className={styles.mobileActionsRowRight}>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={handleCancelEdit}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={handleSaveEdit}
                            disabled={!characterLengthCheck(newCategoryName, MAX_CATEGORY_NAME_LENGTH, { trim: true }) || loading || !categoryToEdit}
                        >
                            <i className="fa-regular fa-save" />
                            Save
                        </button>
                    </div>
                    {/* Divider */}
                    <div className={styles.mobileDivider} />
                    {/* Row 3: Delete */}
                    {!confirmingDelete && (
                        <div className={styles.mobileActionsRowRight}>
                            <button
                                type="button"
                                className={styles.btnDanger}
                                onClick={() => setConfirmingDelete(true)}
                                disabled={loading || (selectedCategory?.id === appliedCategoryId)}
                            >
                                <i className="fa-regular fa-trash" />
                                Delete
                            </button>
                        </div>
                    )}
                    {/* Blocking delete when applied to current QR */}
                    {selectedCategory?.id === appliedCategoryId && (
                        <div className={styles.deleteConfirmNote}>This category is currently applied to this QR. Change the QR category before deleting.</div>
                    )}
                    {confirmingDelete && selectedCategory?.id !== appliedCategoryId && (
                        <>
                            <div className={styles.deleteConfirmNote}>
                                {categoryQrCounts.get(selectedCategory?.id) || 0} QR{(categoryQrCounts.get(selectedCategory?.id) || 0) !== 1 ? 's' : ''} use this category. This will permanently delete the category. This action cannot be undone.
                            </div>
                            <div className={styles.mobileActionsRowRight}>
                                <button
                                    type="button"
                                    className={styles.btnSecondary}
                                    onClick={() => setConfirmingDelete(false)}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={styles.btnDanger}
                                    onClick={() => handleDeleteCategory(selectedCategory)}
                                    disabled={loading}
                                >
                                    Confirm Delete
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>

        {/* Desktop View - Split Layout */}
        <div className={styles.desktopView}>
            {/* Left/Center: Category List */}
            <div className={styles.categoryList}>
                <h4 className={styles.listTitle}>Categories</h4>
                <div className={styles.scrollableList}>
                    {sortedCategories.length === 0 && (
                        <div className={styles.emptyState}>No categories yet</div>
                    )}
                    {sortedCategories.map(cat => (
                        <div 
                            key={cat.id}
                            className={`${styles.listItem} ${selectedCategory?.id === cat.id ? styles.active : ''} ${categoryToEdit?.id === cat.id ? styles.editing : ''}`}
                            onClick={() => handleListItemClick(cat)}
                        >
                            <span className={styles.categoryName}>{cat.name}</span>
                            {selectedCategory?.id === cat.id && (
                                <i className="fa-solid fa-check" />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Right: Management Panel */}
            <div className={styles.managementPanel}>
                {successMsg && (
                    <div className={styles.successToast}>{successMsg}</div>
                )}
                {!editMode && !addNewCategory && (
                    <div className={styles.panelDefault}>
                        <h4 className={styles.panelTitle}>Category Actions</h4>
                        <p className={styles.panelHint}>Select a category to edit or delete</p>
                        {allowNewCategories && (
                            <button
                                type="button"
                                className={styles.btnPrimary}
                                onClick={() => {
                                    setAddNewCategory(true);
                                    setEditMode(false);
                                    setNewCategoryName('');
                                    setError('');
                                }}
                            >
                                <i className="fa-regular fa-plus" />
                                Add New Category
                            </button>
                        )}
                    </div>
                )}

                {addNewCategory && (
                    <div className={styles.panelAdd}>
                        <h4 className={styles.panelTitle}>Add New Category</h4>
                        <div className={styles.inputGroup}>
                            <input
                                id='desktop-new-cat-input'
                                className={styles.input}
                                value={newCategoryName}
                                onChange={handleNewCategoryNameChange}
                                disabled={loading}
                                    maxLength={MAX_CATEGORY_NAME_LENGTH}
                                placeholder="Category name"
                            />
                        </div>
                        {error && <div className={styles.error}>{error}</div>}
                        <div className={styles.panelActions}>
                            <button
                                type="button"
                                className={styles.btnSecondary}
                                onClick={() => {
                                    setAddNewCategory(false);
                                    setNewCategoryName('');
                                    setError('');
                                }}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.btnPrimary}
                                onClick={handleAddNewCategory}
                                    disabled={!characterLengthCheck(newCategoryName, MAX_CATEGORY_NAME_LENGTH, { trim: true }) || loading}
                            >
                                <i className='fa-regular fa-plus' />
                                Add
                            </button>
                        </div>
                    </div>
                )}

                {editMode && categoryToEdit && (
                    <div className={styles.panelEdit}>
                        <div className={styles.panelContent}>
                            <h4 className={styles.panelTitle}>Edit Category</h4>
                            <div className={styles.inputGroup}>
                                <input
                                    id='desktop-edit-cat-input'
                                    className={styles.input}
                                    value={newCategoryName}
                                    onChange={handleNewCategoryNameChange}
                                    disabled={loading}
                                    maxLength={MAX_CATEGORY_NAME_LENGTH}
                                    placeholder="Category name"
                                />
                            </div>
                            {error && <div className={styles.error}>{error}</div>}
                            {/* Row 1: Apply left, Cancel/Save right */}
                            <div className={styles.actionsRowSpaced}>
                                <button
                                    type="button"
                                    className={styles.btnPrimary}
                                    onClick={() => handleApplyCategoryToQR(categoryToEdit)}
                                    disabled={!characterLengthCheck(newCategoryName, MAX_CATEGORY_NAME_LENGTH, { trim: true }) || loading}
                                >
                                    <i className="fa-solid fa-check" />
                                    Apply to QR
                                </button>
                                <div className={styles.actionsGroup}>
                                    <button
                                        type="button"
                                        className={styles.btnSecondary}
                                        onClick={handleCancelEdit}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.btnPrimary}
                                        onClick={handleSaveEdit}
                                        disabled={!characterLengthCheck(newCategoryName, MAX_CATEGORY_NAME_LENGTH, { trim: true }) || loading}
                                    >
                                        <i className="fa-regular fa-save" />
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* Delete section at bottom */}
                        <div className={styles.deleteSection}>
                            {!confirmingDelete && (
                                <div className={styles.actionsRow}>
                                    <button
                                        type="button"
                                        className={styles.btnDanger}
                                        onClick={() => setConfirmingDelete(true)}
                                        disabled={loading || (categoryToEdit?.id === appliedCategoryId)}
                                    >
                                        <i className="fa-regular fa-trash" />
                                        Delete Category
                                    </button>
                                </div>
                            )}
                            {(categoryToEdit?.id === appliedCategoryId) && (
                                <div className={styles.deleteConfirmNote}>This category is currently applied to this QR. Change the QR category before deleting.</div>
                            )}
                            {confirmingDelete && (categoryToEdit?.id !== appliedCategoryId) && (
                                <>
                                    <div className={styles.deleteConfirmNote}>
                                        {categoryQrCounts.get(categoryToEdit?.id) || 0} QR{(categoryQrCounts.get(categoryToEdit?.id) || 0) !== 1 ? 's' : ''} use this category. This will permanently delete the category. This action cannot be undone.
                                    </div>
                                    <div className={styles.actionsRow}>
                                        <button
                                            type="button"
                                            className={styles.btnSecondary}
                                            onClick={() => setConfirmingDelete(false)}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.btnDanger}
                                            onClick={() => handleDeleteCategory(categoryToEdit)}
                                            disabled={loading}
                                        >
                                            Confirm Delete
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
};